/**
 * Week maths in the viewer's local timezone.
 *
 * The server stores a week as the ISO date of its Monday and never guesses
 * which week "now" falls in -- the client decides, so a family in UTC-7 does
 * not flip to next week at 5pm on Sunday.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** Parse `YYYY-MM-DD` as a local date, not UTC midnight. */
export function parseISO(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return new Date(y, m - 1, d)
}

export function toISO(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** The Monday on or before `date`. Sunday counts as the coming week's Monday. */
export function mondayOf(date: Date = new Date()): string {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const day = d.getDay() // 0 = Sunday
  // Sunday rolls forward to tomorrow; everything else rolls back.
  const delta = day === 0 ? 1 : 1 - day
  d.setDate(d.getDate() + delta)
  return toISO(d)
}

export function addWeeks(iso: string, weeks: number): string {
  const d = parseISO(iso)
  d.setDate(d.getDate() + weeks * 7)
  return toISO(d)
}

/** The date of a given weekday (1 = Monday) within a week. */
export function dayDate(weekStart: string, dayOfWeek: number): Date {
  const d = parseISO(weekStart)
  d.setDate(d.getDate() + (dayOfWeek - 1))
  return d
}

/** "Sep 14 – 18" or "Sep 28 – Oct 2" when the week straddles a month. */
export function formatWeekRange(weekStart: string): string {
  const start = parseISO(weekStart)
  const end = dayDate(weekStart, 5)
  const left = `${MONTHS[start.getMonth()]} ${start.getDate()}`
  const right =
    start.getMonth() === end.getMonth()
      ? `${end.getDate()}`
      : `${MONTHS[end.getMonth()]} ${end.getDate()}`
  return `${left} – ${right}`
}

export function formatDayDate(weekStart: string, dayOfWeek: number): string {
  const d = dayDate(weekStart, dayOfWeek)
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

/** Relative label for the week list: "This week", "Next week", "2 weeks ago". */
export function relativeWeekLabel(weekStart: string, today: Date = new Date()): string {
  const current = parseISO(mondayOf(today)).getTime()
  const target = parseISO(weekStart).getTime()
  const diff = Math.round((target - current) / (7 * 24 * 60 * 60 * 1000))
  if (diff === 0) return 'This week'
  if (diff === 1) return 'Next week'
  if (diff === -1) return 'Last week'
  if (diff > 1) return `In ${diff} weeks`
  return `${Math.abs(diff)} weeks ago`
}

export function isSameWeek(weekStart: string, today: Date = new Date()): boolean {
  return weekStart === mondayOf(today)
}
